//? Types
import type {
	LifecycleAlternativeStat,
	LifecycleApplicationUsage,
} from "../../../hooks/questions/types/types";
import type { ApexOptions } from "apexcharts";

//* React hooks
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

//* Data hooks
import { useQuestionLifecycle } from "../../../hooks/questions/useQuestionLifecycle";

//* External components
import ReactApexChart from "react-apexcharts";
import { Button } from "react-bootstrap";
import { CircularProgress } from "@material-ui/core";
import { RiArrowGoBackLine } from "react-icons/ri";
import moment from "moment";

const formatDateTime = (value: string | null): string => {
	if (!value) {
		return "-";
	}
	return moment(value).format("DD/MM/YYYY HH:mm");
};

const formatChartDate = (value: string | number): string => {
	const parsed = moment(value);
	return parsed.isValid() ? parsed.format("DD/MM") : String(value);
};

export function QuestionLifecycleView() {
	const token = localStorage.getItem("token") || "";
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();

	// Selected application for the per-application breakdown ("" = none/general).
	const [applicationId, setApplicationId] = useState<string>("");

	const { lifecycle, loading, fetching, isError } = useQuestionLifecycle(
		token,
		id,
		applicationId || undefined
	);

	const renderAlternativesTable = (alternatives: LifecycleAlternativeStat[]) => (
		<div className="table-rep-plugin">
			<div className="table-responsive mb-0" data-pattern="priority-columns">
				<table className="table table-striped mb-0">
					<thead>
						<tr>
							<th>Alternativa</th>
							<th>Quantidade</th>
							<th>Percentual</th>
							<th>Tipo</th>
						</tr>
					</thead>
					<tbody>
						{alternatives.map((item) => (
							<tr key={item.order}>
								<td>{item.letter}</td>
								<td>{item.count}</td>
								<td>{item.percentage}%</td>
								<td>
									{item.isCorrect ? (
										<span className="badge bg-success">Correta</span>
									) : (
										<span className="badge bg-danger">Incorreta</span>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);

	const buildAlternativesChart = (): { options: ApexOptions; series: ApexAxisChartSeries } => {
		const categories = lifecycle?.alternativesTimeseries?.dates || [];
		const series = (lifecycle?.alternativesTimeseries?.series || []).map((serie) => ({
			name: `Alternativa ${serie.letter}${serie.isCorrect ? " (Correta)" : ""}`,
			data: serie.values || [],
		}));

		return {
			options: {
				chart: { toolbar: { show: true }, zoom: { enabled: true } },
				xaxis: {
					categories,
					title: { text: "Data" },
					labels: { formatter: formatChartDate },
				},
				yaxis: { title: { text: "Quantidade de respostas" }, min: 0 },
				stroke: { curve: "smooth", width: 3 },
				dataLabels: { enabled: false },
				markers: { size: 4, hover: { size: 6 } },
				grid: { borderColor: "#eef0f2" },
				legend: { position: "top" },
				noData: { text: "Sem dados para gráfico de alternativas" },
				tooltip: {
					x: { formatter: (val) => formatChartDate(val) },
					y: { formatter: (val) => `${val} resposta(s)` },
				},
			},
			series,
		};
	};

	const buildPerformanceChart = (): { options: ApexOptions; series: ApexAxisChartSeries } => {
		const categories = lifecycle?.performanceTimeseries?.dates || [];

		return {
			options: {
				chart: { toolbar: { show: true }, zoom: { enabled: true } },
				xaxis: {
					categories,
					title: { text: "Data" },
					labels: { formatter: formatChartDate },
				},
				yaxis: { title: { text: "Percentual (%)" }, min: 0, max: 100 },
				stroke: { curve: "smooth", width: 3 },
				dataLabels: { enabled: false },
				markers: { size: 4, hover: { size: 6 } },
				grid: { borderColor: "#eef0f2" },
				colors: ["#28a745", "#dc3545"],
				legend: { position: "top" },
				noData: { text: "Sem dados para gráfico de desempenho" },
				tooltip: {
					x: { formatter: (val) => formatChartDate(val) },
					y: { formatter: (val) => `${val}%` },
				},
			},
			series: [
				{ name: "% Acerto", data: lifecycle?.performanceTimeseries?.accuracyPercentage || [] },
				{ name: "% Erro", data: lifecycle?.performanceTimeseries?.errorPercentage || [] },
			],
		};
	};

	const buildDispersionChart = (): { options: ApexOptions; series: ApexAxisChartSeries } => {
		const applications = lifecycle?.dispersion?.perApplication || [];
		const points = applications.map((item) => ({
			x: item.applicationName || `Aplicação ${item.applicationId ?? "-"}`,
			y: Number(item.errorPercentage || 0),
		}));

		return {
			options: {
				chart: { toolbar: { show: true }, zoom: { enabled: true } },
				xaxis: {
					type: "category",
					title: { text: "Aplicações" },
					labels: { rotate: -30, trim: true },
				},
				yaxis: { title: { text: "% Erro" }, min: 0, max: 100 },
				dataLabels: { enabled: false },
				markers: { size: 6, hover: { size: 8 } },
				grid: { borderColor: "#eef0f2" },
				noData: { text: "Sem dados para dispersão de erro" },
				tooltip: {
					x: { formatter: (val) => `${val}` },
					y: { formatter: (val) => `${val}%` },
				},
			},
			series: [{ name: "Dispersão % Erro", data: points }],
		};
	};

	const alternativesChart = buildAlternativesChart();
	const performanceChart = buildPerformanceChart();
	const dispersionChart = buildDispersionChart();

	const applications: LifecycleApplicationUsage[] = lifecycle?.usage?.applications || [];
	const hasTimeSeries = (lifecycle?.alternativesTimeseries?.dates || []).length > 0;
	const hasDispersion = (lifecycle?.dispersion?.perApplication || []).length > 0;

	return (
		<div className="row">
			<div className="col-12">
				<div className="card">
					<div className="card-body" id="card">
						<div style={{ float: "right" }}>
							<Button
								id="btn-lifecycle-back"
								type="button"
								variant="secondary"
								onClick={() => navigate("/questions")}
							>
								<RiArrowGoBackLine /> Voltar
							</Button>
						</div>

						<h4 className="mt-0 header-title" style={{ fontSize: "1.5rem", color: "black" }}>
							Ciclo de Vida da Questão
						</h4>
						<p style={{ fontSize: "1rem", color: "black" }}>
							Uso em aplicações e desempenho de respostas da questão.
						</p>
						<br />

						{loading && (
							<div className="row" style={{ margin: 0 }}>
								<div
									className="col-12 d-flex justify-content-center align-items-center"
									style={{ minHeight: 120 }}
								>
									<CircularProgress />
								</div>
							</div>
						)}

						{!loading && isError && (
							<div className="alert alert-danger" role="alert" style={{ whiteSpace: "pre-line" }}>
								Erro ao carregar o ciclo de vida da questão.
							</div>
						)}

						{!loading && !isError && lifecycle && (
							<>
								<div className="row" style={{ marginBottom: 16 }}>
									<div className="col-12">
										<div className="card" style={{ marginBottom: 0 }}>
											<div className="card-body">
												<h4 className="mt-0 header-title" style={{ fontSize: "1.2rem" }}>
													Questão
												</h4>
												<p style={{ marginBottom: 8 }}>
													<strong>ID da questão:</strong> {lifecycle.question?.id}
												</p>
												<p style={{ marginBottom: 0 }}>
													<strong>Enunciado:</strong> {lifecycle.question?.statement || "-"}
												</p>
											</div>
										</div>
									</div>
								</div>

								<div className="row" style={{ marginBottom: 16 }}>
									<div className="col-12 col-md-4">
										<div className="card" style={{ marginBottom: 0 }}>
											<div className="card-body">
												<h6 className="header-title" style={{ marginBottom: 6 }}>
													Usos em provas
												</h6>
												<p style={{ fontSize: 24, margin: 0, color: "#495057" }}>
													{lifecycle.usage?.distinctTests || 0}
												</p>
												<small>Provas distintas que já incluíram a questão</small>
											</div>
										</div>
									</div>
									<div className="col-12 col-md-4">
										<div className="card" style={{ marginBottom: 0 }}>
											<div className="card-body">
												<h6 className="header-title" style={{ marginBottom: 6 }}>
													% Acerto (Geral)
												</h6>
												<p style={{ fontSize: 24, margin: 0, color: "green" }}>
													{lifecycle.generalStats?.accuracyPercentage || 0}%
												</p>
											</div>
										</div>
									</div>
									<div className="col-12 col-md-4">
										<div className="card" style={{ marginBottom: 0 }}>
											<div className="card-body">
												<h6 className="header-title" style={{ marginBottom: 6 }}>
													% Erro (Geral)
												</h6>
												<p style={{ fontSize: 24, margin: 0, color: "red" }}>
													{lifecycle.generalStats?.errorPercentage || 0}%
												</p>
											</div>
										</div>
									</div>
								</div>

								<div className="card" style={{ marginBottom: 16 }}>
									<div className="card-body">
										<h4 className="mt-0 header-title" style={{ fontSize: "1.2rem" }}>
											Dispersão e Desvio Padrão
										</h4>
										<div className="row">
											<div className="col-12 col-md-4">
												<p style={{ marginBottom: 8 }}>
													<strong>Média % Acerto:</strong>{" "}
													{lifecycle.dispersion?.meanAccuracyPercentage || 0}%
												</p>
												<p style={{ marginBottom: 8 }}>
													<strong>Média % Erro:</strong>{" "}
													{lifecycle.dispersion?.meanErrorPercentage || 0}%
												</p>
												<p style={{ marginBottom: 8 }}>
													<strong>Média total respostas:</strong>{" "}
													{lifecycle.dispersion?.meanTotalResponses || 0}
												</p>
											</div>
											<div className="col-12 col-md-4">
												<p style={{ marginBottom: 8 }}>
													<strong>Desvio padrão % Acerto:</strong>{" "}
													{lifecycle.dispersion?.stddevAccuracyPercentage || 0}
												</p>
												<p style={{ marginBottom: 8 }}>
													<strong>Desvio padrão % Erro:</strong>{" "}
													{lifecycle.dispersion?.stddevErrorPercentage || 0}
												</p>
												<p style={{ marginBottom: 8 }}>
													<strong>Desvio padrão total respostas:</strong>{" "}
													{lifecycle.dispersion?.stddevTotalResponses || 0}
												</p>
											</div>
											<div className="col-12 col-md-4">
												<p style={{ marginBottom: 8 }}>
													<strong>Quantidade de aplicações:</strong>{" "}
													{lifecycle.dispersion?.applicationCount || 0}
												</p>
											</div>
										</div>

										{hasDispersion && (
											<div style={{ marginTop: 16 }}>
												<ReactApexChart
													options={dispersionChart.options}
													series={dispersionChart.series}
													type="scatter"
													height={320}
												/>
											</div>
										)}
									</div>
								</div>

								<div className="card" style={{ marginBottom: 16 }}>
									<div className="card-body">
										<h4 className="mt-0 header-title" style={{ fontSize: "1.2rem" }}>
											Tendência Temporal
										</h4>
										<p style={{ marginBottom: 12 }}>
											Evolução das respostas por alternativa e desempenho por data.
										</p>

										{hasTimeSeries ? (
											<>
												<div style={{ marginBottom: 24 }}>
													<ReactApexChart
														options={alternativesChart.options}
														series={alternativesChart.series}
														type="line"
														height={330}
													/>
												</div>
												<div>
													<ReactApexChart
														options={performanceChart.options}
														series={performanceChart.series}
														type="line"
														height={330}
													/>
												</div>
											</>
										) : (
											<div className="alert alert-light" role="alert" style={{ marginBottom: 0 }}>
												Sem dados suficientes para montar séries temporais.
											</div>
										)}
									</div>
								</div>

								<div className="card" style={{ marginBottom: 16 }}>
									<div className="card-body">
										<h4 className="mt-0 header-title" style={{ fontSize: "1.2rem" }}>
											Datas de Uso em Aplicações
										</h4>
										{applications.length === 0 ? (
											<div className="alert alert-light" role="alert" style={{ marginBottom: 0 }}>
												Nenhuma utilização em aplicação encontrada.
											</div>
										) : (
											<div className="table-rep-plugin">
												<div
													className="table-responsive mb-0"
													data-pattern="priority-columns"
												>
													<table className="table table-striped mb-0">
														<thead>
															<tr>
																<th>Aplicação</th>
																<th>Data referência de uso</th>
																<th>Primeira resposta</th>
																<th>Última resposta</th>
																<th>% Acerto</th>
																<th>% Erro</th>
															</tr>
														</thead>
														<tbody>
															{applications.map((item, index) => (
																<tr key={item.applicationId ?? index}>
																	<td>
																		{item.applicationName ||
																			`Aplicação ${item.applicationId ?? "-"}`}
																	</td>
																	<td>{formatDateTime(item.referenceDate)}</td>
																	<td>{formatDateTime(item.firstResponseAt)}</td>
																	<td>{formatDateTime(item.lastResponseAt)}</td>
																	<td>{item.accuracyPercentage || 0}%</td>
																	<td>{item.errorPercentage || 0}%</td>
																</tr>
															))}
														</tbody>
													</table>
												</div>
											</div>
										)}
									</div>
								</div>

								<div className="card" style={{ marginBottom: 16 }}>
									<div className="card-body">
										<h4 className="mt-0 header-title" style={{ fontSize: "1.2rem" }}>
											Estatísticas Gerais
										</h4>
										<p style={{ marginBottom: 8 }}>
											<strong>Total de respostas:</strong>{" "}
											{lifecycle.generalStats?.totalResponses || 0}
										</p>
										<p style={{ marginBottom: 8 }}>
											<strong>Total de corretas:</strong>{" "}
											{lifecycle.generalStats?.totalCorrect || 0}
										</p>
										<p style={{ marginBottom: 12 }}>
											<strong>Total de erradas:</strong>{" "}
											{lifecycle.generalStats?.totalWrong || 0}
										</p>
										{renderAlternativesTable(lifecycle.generalStats?.alternatives || [])}
									</div>
								</div>

								<div className="card" style={{ marginBottom: 16 }}>
									<div className="card-body">
										<h4 className="mt-0 header-title" style={{ fontSize: "1.2rem" }}>
											Estatísticas por Aplicação
										</h4>
										<p style={{ marginBottom: 12 }}>
											Selecione uma aplicação para filtrar os números só daquela utilização.
										</p>

										<div className="row" style={{ marginBottom: 16 }}>
											<div className="col-12 col-md-6 mb-2 mb-md-0">
												<select
													id="select-lifecycle-application"
													className="form-control"
													value={applicationId}
													onChange={(e) => setApplicationId(e.target.value)}
												>
													<option value="">Selecione uma aplicação</option>
													{applications
														.filter((item) => item.applicationId)
														.map((item) => (
															<option
																key={item.applicationId as string}
																value={item.applicationId as string}
															>
																{item.applicationName ||
																	`Aplicação ${item.applicationId}`}
															</option>
														))}
												</select>
											</div>
											<div
												className="col-12 col-md-6 d-flex align-items-center"
												style={{ gap: 8, flexWrap: "wrap" }}
											>
												<Button
													id="btn-lifecycle-clear-application"
													type="button"
													variant="secondary"
													disabled={fetching || !applicationId}
													onClick={() => setApplicationId("")}
												>
													Limpar
												</Button>
												{fetching && <span>Buscando...</span>}
											</div>
										</div>

										{lifecycle.applicationStats == null ? (
											<div className="alert alert-light" role="alert" style={{ marginBottom: 0 }}>
												Nenhuma aplicação selecionada.
											</div>
										) : (
											<div className="card" style={{ marginBottom: 0 }}>
												<div className="card-body">
													<p style={{ marginBottom: 8 }}>
														<strong>Aplicação ID:</strong>{" "}
														{lifecycle.applicationStats.applicationId}
													</p>
													<p style={{ marginBottom: 8 }}>
														<strong>Total de respostas:</strong>{" "}
														{lifecycle.applicationStats.totalResponses || 0}
													</p>
													<p style={{ marginBottom: 8 }}>
														<strong>% Acerto:</strong>{" "}
														<span style={{ color: "green" }}>
															{lifecycle.applicationStats.accuracyPercentage || 0}%
														</span>
													</p>
													<p style={{ marginBottom: 12 }}>
														<strong>% Erro:</strong>{" "}
														<span style={{ color: "red" }}>
															{lifecycle.applicationStats.errorPercentage || 0}%
														</span>
													</p>
													{renderAlternativesTable(
														lifecycle.applicationStats.alternatives || []
													)}
												</div>
											</div>
										)}
									</div>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default QuestionLifecycleView;
